/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Box, Button, useTheme } from '@mui/material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { tomorrowNightBright } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { CSVLink } from 'react-csv';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import styled from '@emotion/styled';
import { sanitizeHtml, sanitizeMermaid, checkHref, unEscapeHTML } from '../Utils/text_functions';
import { FiPieChart, FiCopy, FiDownload } from "react-icons/fi";
import * as log from 'loglevel';
import mermaid from 'mermaid';
import Plot from 'react-plotly.js';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { ReactVirtualizedTable } from './Components/VirtualizedTable';
import { AccordionFileComponent } from '../Components/AccordionFileComponent';
import { ImageComponent } from '../Components/ImageComponent';
import { LuLoaderCircle } from 'react-icons/lu';
import { v4 as uuidv4 } from 'uuid';
import { DeepAgent } from './DeepAgent/DeepAgent'

const Code = styled.div`
  padding: 0;
  margin-top: 10px;
  border-radius: 0.75rem;
  overflow: hidden;
  background: black;

  & > div {
    margin: 0 !important;
  }

  .fa {
    font-style: normal !important;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;

  button {
    display: flex;
    align-items: center;
    padding: 4px !important;
    margin: 5px;
  } 
`;

const ImagePreview = styled.div`
  text-align: center;

  img {
    max-width: 30rem !important;
    display: block;
  }
`;

const MarkdownRenderer = ({ deepAgentSidebarRef=null, content, user, setDeepAgentFinalResponse, type, addOrUpdatFinalMessage=()=>{}, handleChartUpdate=async () => null, isChartRendering=false, default_data, getToken }) => {
  const theme = useTheme();
  useEffect(() => {
    mermaid.initialize({ startOnLoad: false, theme: "forest" });
  }, []);

  const extractTableData = useCallback((tableNode) => {
    var rows = [];
    if (!tableNode.children) {
      return rows; 
    }
    try {
      tableNode.children.forEach((child) => {
        if (child.tagName === 'thead' || child.tagName === 'tbody') {
          if (child.children !== null && child.children !== undefined) {          
            child.children.forEach((row) => {
              let cells = [];
              for (let i = 0; i < row.children.length; i++) {
                const cell = row.children[i];
                if (cell.children && cell.children.length > 0) {
                  const firstChild = cell.children[0];
                  if (firstChild.children && firstChild.children.length > 0) {
                    const secondChild = firstChild.children[0];
                    if (secondChild && secondChild.tagName === 'img') {
                      cells.push(secondChild.properties.src);
                    } else if (secondChild) {
                      cells.push(secondChild.value);
                    } else {
                      cells.push(firstChild.value);
                    }
                  } else {
                    cells.push(firstChild.value);
                  }
                }
              }
              rows.push(cells);
          });
          }
        }
      });
    } catch (e) {
      log.error(`/MarkdownRenderer: ${e.message}`);
    }
    return rows;
  }, []);


  const schema = useMemo(() => ({
    tagNames: ['table', 'thead', 'tbody', 'tr', 'th', 'td', 'ins', 'del', 'p', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'blockquote', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'file-content', 'action', 'comment', 'gen-images', 'deep-agent'],
    attributes: {
      a: ['href', 'title', 'target'],
      ins: [],
      del: []
    }
  }), []);

  const CustomComponent = ({ node, ...props }) => {
    if (React.Children.count(props.children) === 0) return null;

    const children = React.Children.map(props.children, (child) => {
      if (typeof child === 'string') {
        return unEscapeHTML(child);
      }
      return child;
    });

    if (node.tagName === 'p') {
      return (
        <div>
          {children.map((child, index) => {            
            if (typeof child === 'string') {
              let child_trimmed = child.trim();
              if(child_trimmed.length > 5 && child_trimmed.indexOf('$$') !== -1) {
                if (child_trimmed.startsWith('$$') && child_trimmed.endsWith('$$')) {
                  return <BlockMath>{sanitizeHtml(child.replace(/\$\$/g, ''))}</BlockMath>
                }
                else {
                  // find all occurences of $$...$$ and replace with InlineMath
                  const parts = child.split(/(\$\$[\s\S]*?\$\$)/g);
                  return parts.map((part, i) => {
                    if (part.startsWith('$$') && part.endsWith('$$')) {
                      return <InlineMath key={`${index}-${i}`}>{sanitizeHtml(part.replace(/\$\$/g, ''))}</InlineMath>;
                    }
                    return <span key={`${index}-${i}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(part).replace(/\n/g, '<br />') }} />;
                  });
                }
              }

              return <p style={{ display: 'inline' }} key={index} dangerouslySetInnerHTML={{ __html: sanitizeHtml(child).replace(/\n/g, '<br />') }} />;
            }
            // limit child.type to avoid XSS
            const allowedTypes = ['a', 'strong', 'em', 'del', 'ins', 'code', 'pre', 'span', 'img', 'br'];
            if(child.type === 'a') {
              if(child.props.href) {
                if(!checkHref(child.props.href)) {
                  return null;
                }
                // Ensure target="_blank" is added to all <a> tags
                return React.cloneElement(child, { target: '_blank' });                
              }
            }
            else if(child.type === 'img') {
              if(child.props.src) {
                if(!checkHref(child.props.src)) {
                  return null;
                }
              }
            }
            if (child.type && !allowedTypes.includes(child.type)) {
                if(child.props && child.props.node && allowedTypes.includes(child.props.node.tagName)) {
                }
                else {
                  return null;
                }
            }
            return <span style={{ display: 'inline' }} key={index} className="inline-item">{child}</span>;
          })}
        </div>
      );
    }

    return React.createElement(node.tagName, props, children);
  };

  const preprocessContent = (content) => {
    if (!content) return '';

    content = content.replace(/```markdown([\s\S]*?)```/g, '$1');
    // Replace content inside <file-content> tags with a placeholder
    return content.replace(/<file-content>\n([\s\S]*?)<\/file-content>/g, (match, p1) => {
      return `<file-content>\n${encodeURIComponent(p1)}</file-content>`;
    })
    .replace(/<plotly-json>\n([\s\S]*?)<\/plotly-json>/g, (match, p1) => {
      return `<plotly-json>\n${encodeURIComponent(p1)}</plotly-json>`;
    })
  };

  const preprocessedContent = preprocessContent(content);

  return (
    <>
      {user === 'me' ? 
        <Box>
            <p style={{ display: 'inline', fontSize: '11pt' }} dangerouslySetInnerHTML={{ __html: preprocessedContent.replace(/\n/g, '<br />') }} />
        </Box> 
      :
      <Box className="markdown-div">
        <ReactMarkdown
          children={preprocessedContent}
          rehypePlugins={[rehypeRaw, rehypeSanitize(schema)]}
          remarkPlugins={[remarkGfm]}
          escapeHTML={false}
          components={{
            code: ({ node, inline, className, children, ...props }) => (
              <CodeComponent
                node={node}
                inline={inline}
                className={className}
                handleChartUpdate={handleChartUpdate}
                isChartRendering={isChartRendering}
                {...props}
              >
                {children}
              </CodeComponent>
            ),
            a: ({ node, ...props }) => {
              // Validate href using checkHref
              if (!checkHref(props.href)) {
                return null; // Do not render invalid links
              }
              // Ensure all <a> tags include target="_blank" and rel="noopener noreferrer"
              return <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>;
            },
            img: ({ node, ...props }) => {
              // Validate src using checkHref
              if (!checkHref(props.src)) {
                return null; // Do not render invalid images
              }
              // Render the <img> tag with validated src
              return <img {...props} alt={props.alt || ''} />;
            },            
            'file-content': ({ node, children, ...props }) => {
              const contentString = decodeURIComponent(React.Children.toArray(children).join(''));
              return (<AccordionFileComponent content={contentString} />);
            },
            'deep-agent': ({ node, children, ...props }) => {
              if (type && type === 'deepagent'){
                return null
              }
              // check if id is in props
              if (props['id'] === undefined) {
                log.error('DeepAgent id not found in props');
                return null;
              }

              // get id in props              
              const deepAgentId = props['id'];
              return (
                <DeepAgent
                  default_data={default_data}
                  getToken={getToken}
                  deepAgentSidebarRef={deepAgentSidebarRef}
                  deep_agent_id={deepAgentId}
                  setDeepAgentFinalResponse={setDeepAgentFinalResponse}
                  addOrUpdatFinalMessage={addOrUpdatFinalMessage}
                />
              )
            },
            'gen-images': ({ node, children, ...props }) => {
              return (<ImageComponent content={children} />)
            },
            'plotly-json': ({ node, children, ...props }) => {
              const contentString = decodeURIComponent(React.Children.toArray(children).join(''));
              try {
                var plot = JSON.parse(contentString);              
                return (<PlotlyComponent plot={plot} />);
              }
              catch (e) {
                log.error(`/MarkdownRenderer: 2: ${e.message}`);
                return null;
              }
            },
            action: ({ node, children, ...props }) => {
              return (<div className="action_block">{children}</div>)
            },
            comment: ({ node, children, ...props }) => {
              return (<div className="comment_block">{children}</div>)
            },
            table({ node, ...props }) {
              const tableData = extractTableData(node);
              return (
                <Box>
                  <ReactVirtualizedTable data={tableData} />
                  <Header>
                    <CSVLink 
                      style={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        padding: "6px",
                        fontSize: "10pt",
                        height: '35px',
                        border: '1px solid #315399',
                        borderRadius: '5px',
                        textAlign: 'left',
                        transition: 'ease 0.25s all',
                        cursor: 'pointer',
                        margin: '7px',
                        textDecoration: 'none',
                      }} 
                      data={tableData} 
                      filename="export-asksage.csv"
                    >
                      <FiDownload alt="Download CSV" size={16} style={{ marginRight: '5px' }} />
                      <span>Download CSV</span>
                    </CSVLink>
                  </Header>
                </Box>
              );
            },
            p: CustomComponent
          }}
        />
      </Box>
      }
    </>
  );
};

const PlotlyComponent = ({ plot }) => {
  return (
    <Plot data={plot.data} useResizeHandler layout={{ autosize: true, ...plot.layout}} style={{width: "100%", marginBottom: '20px'}}/>
  );
};
  


const CodeComponent = ({ node, inline, className, children, handleChartUpdate, isChartRendering=false, ...props }) => {
  const [showMermaidCode, setShowMermaidCode] = useState(false);
  const idRef = useRef(`mermaid-${uuidv4()}`);
  const reactKey = `code-${node.position?.start.line}`;

  const [copied, setCopied] = useState(false);

  const [mermaidSVG, setMermaidSVG] = useState('');
  const handleCopy = useCallback(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const match = /language-(\w+)/.exec(className || '');
  if (!children) return null;

  let code = String(children).replace(/\n$/, '');
  code = unEscapeHTML(code);

  let isMermaid = false
  if(className) {
    isMermaid = className.includes("language-mermaid");
  }

  return !inline && match ? (
    <>
      {isMermaid ? (
        <>
          <Mermaid key={reactKey} diagramId={idRef.current} content={String(children).trim()} onRender={setMermaidSVG} handleChartUpdate={() => handleChartUpdate(code)} isChartRendering={isChartRendering} />
          <Header>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowMermaidCode(!showMermaidCode)}
            >
              <FiPieChart alt="Toggle Code" size={16} style={{ marginRight: '5px' }} />
              {showMermaidCode ? 'Hide Mermaid Code' : 'Show Mermaid Code'}
            </Button>
            {mermaidSVG && (
              <Button
                variant="contained"
                color="primary"
                style={{ minWidth: 0, padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                onClick={() => {
                  const blob = new Blob([mermaidSVG], { type: 'image/svg+xml' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'mermaid-diagram.svg';
                  a.click();
                  // Clean up the URL after downloading
                  URL.revokeObjectURL(url);
                }}
              >
                <FiDownload alt="Download Diagram" size={16} style={{ marginRight: '5px' }} />
                <span>Download</span>
              </Button>
            )}
          </Header>
          {showMermaidCode && (
            <Code>
              <SyntaxHighlighter
                style={{
                  ...tomorrowNightBright,
                  hljs: {
                    ...tomorrowNightBright.hljs,
                    padding: '15px',
                  }
                }}    
                language={match?.[1] || 'text'}
                PreTag="div"
                {...props}
              >
                {code}
              </SyntaxHighlighter>
              <Header>
                <CopyToClipboard text={code} onCopy={handleCopy}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ minWidth: 0, padding: '2px 8px', display: 'flex', alignItems: 'center' }}
                    aria-label="Copy code"
                  >
                    {copied ? 'Copied!' : <span><FiCopy alt="Copy" size={16} /> Copy</span>}
                  </Button>
                </CopyToClipboard>
              </Header>
            </Code>
          )}
        </>
      ) : (
        <>
        <Code>
          <SyntaxHighlighter
            style={{
              ...tomorrowNightBright,
              hljs: {
                ...tomorrowNightBright.hljs,
                padding: '15px',
              }
            }}    
            language={match?.[1] || 'text'}
            PreTag="div"
            {...props}
          >
            {code}
          </SyntaxHighlighter>
          <Header>
            <CopyToClipboard text={code} onCopy={handleCopy}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ minWidth: 0, padding: '2px 8px', display: 'flex', alignItems: 'center' }}
                aria-label="Copy code"
              >
                {copied ? 'Copied!' : <span><FiCopy alt="Copy" size={16} /> Copy</span>}
              </Button>
            </CopyToClipboard>
            {code.startsWith('<svg') && code.includes('</svg>') && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ minWidth: 0, padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                onClick={() => {
                  const blob = new Blob([code], { type: 'image/svg+xml' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'image.svg';
                  a.click();
                }}
              >
                <FiDownload alt="Download SVG" size={16} style={{ marginRight: '5px' }} />
                <span>Download SVG</span>
              </Button>
            )}
          </Header>
        </Code>
        {code.startsWith('<svg') && code.includes('</svg>') && (
          <ImagePreview>
            <img alt="code" src={`data:image/svg+xml;base64,${btoa(code)}`} />
          </ImagePreview>
        )}
        </>
      )}
    </>
  ) : (
    <code className={className} {...props}>
      {unEscapeHTML(String(children))}
    </code>
  );
};

// Removes any <script> blocks for safety
// Lets the browser decode HTML entities
const decodeEntities = (str) => {
  if (typeof str !== 'string') return '';
  
  const element = document.createElement('div');
  str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gmi, '');
  element.innerHTML = str;
  return element.textContent;
};

// A custom component to render a Mermaid diagram given the string.
const Mermaid = ({ content, onRender, handleChartUpdate, isChartRendering}) => {
  const [diagram, setDiagram] = useState(true);
  const [error, setError] = useState(null);

  // Remove ```mermaid fences that sometimes wrap the code
  const stripMermaidFence = (src = '') =>
    src
      .replace(/^[\s`]*```mermaid[\t ]*\n?/i, '')
      .replace(/\n?[\t ]*```[\s`]*$/i, '')
      .trim();

  // Try to parse + render; on success store SVG, on failure log it
  const renderDiagram = useCallback(async (diagramContent) => {
    if (!diagramContent) {
      setError('No diagram content provided');
      setDiagram(null);
      return;
    }

    try {
      const clean = stripMermaidFence(decodeEntities(diagramContent));

      if (!clean) {
        throw new Error('Invalid diagram content after cleaning');
      }

      await mermaid.parse(clean);
      const { svg } = await mermaid.render(`mermaid-${uuidv4()}`, clean);

      if (!svg || typeof svg !== 'string') {
        throw new Error('Invalid SVG output from Mermaid');
      }

      setDiagram(svg);
      if (typeof onRender === 'function') {
        onRender(svg);
      }
      setError(null);
    } catch (error) {
      setError(error?.message ?? 'Mermaid render failed');
      setDiagram(null);
    }
  }, [onRender]);

  // Render whenever the input content changes
  useEffect(() => {
    renderDiagram(content);
  }, [content, renderDiagram]);
  
  // Ask the LLM helper to fix the chart, then re-render
  const handleFixAndRerender = async () => {

    if (typeof handleChartUpdate !== 'function') {
      log.error('handleChartUpdate is not a function');
      return;
    }

    try {
      const fixedContent = await handleChartUpdate(content, error);
      if (fixedContent) {
        await renderDiagram(fixedContent);
      } else {
        log.warn("No fixed content returned from handleChartUpdate")
      }
    } catch (fixError) {
      log.error("Couldn't fix chart:", fixError)
    }
  };

  if (error) {
    return (
      <div>
        <p>An error occurred while rendering the diagram.</p>
        <Button sx={{ marginLeft: 1.2 }} variant='contained' disabled={isChartRendering} endIcon={isChartRendering && <LuLoaderCircle className='spinner' />} onClick={handleFixAndRerender}>
          {isChartRendering ? 'Fixing...' : 'Fix and Rerender Chart'}
        </Button>
      </div>
    );
  }
  if (diagram === false) {
    return <p>Unable to render this diagram.</p>;
  }

  return <div key={content} style={{backgroundColor: 'white'}} dangerouslySetInnerHTML={{ __html: diagram }} />;
};

const MemoizedMarkdownRenderer = React.memo(MarkdownRenderer, (prevProps, nextProps) => {
  return prevProps.content === nextProps.content && prevProps.isChartRendering === nextProps.isChartRendering;
});

export { MarkdownRenderer, MemoizedMarkdownRenderer };
